import React from 'react';
import PropTypes from 'prop-types';
import Translate from '@hydra/common/components/i18n';

import { makeStyles } from '@material-ui/core/styles';

import ProgressBar from '@hydra/common/components/ProgressBar';
import CriteriumCompetitors from '@hydra/common/criterium/CriteriumCompetitors';
import Criterium from '@hydra/common/criterium/Criterium';
import hasCritData from '@hydra/utils/hasCritData';
import { getContrastedColor, getPriSecColors } from '@hydra/utils/colors';
import { useReviewContext } from '@hydra/common/context/review';
import { usePdfTemplateContext } from '@hydra/common/context/pdfTemplate';

const useStyles = makeStyles(theme => ({
  rank: {
    marginBottom: theme.spacing(1)
  }
}));

export const getRanking = rankingData => {
  const thresholds = [
    { above: 5000000, percent: 0, value: <Translate content='very_low' /> },
    { above: 3000000, percent: 10, value: <Translate content='low' /> },
    { above: 1000000, percent: 20, value: <Translate content='low' /> },
    { above: 5000000, percent: 30, value: <Translate content='medium' /> },
    { above: 500000, percent: 40, value: <Translate content='medium' /> },
    { above: 300000, percent: 50, value: <Translate content='medium' /> },
    { above: 200000, percent: 60, value: <Translate content='high' /> },
    { above: 100000, percent: 70, value: <Translate content='high' /> },
    { above: 50000, percent: 80, value: <Translate content='high' /> },
    { above: 100, percent: 90, value: <Translate content='very_high' /> },
    { above: 0, percent: 100, value: <Translate content='very_high' /> }
  ];

  return (rankingData === -1)
    ? thresholds[0]
    : thresholds.find(rank => rank.above <= rankingData);
};

export const Alexa = ({ criterium, competitors = [] }) => {
  const classes = useStyles();
  const { isPdf } = useReviewContext();
  const { templateColors } = usePdfTemplateContext();

  const { primary, secondary } = isPdf
    ? templateColors
    : getPriSecColors();

  const color = getContrastedColor(primary);

  const { data, url } = criterium;

  const singleContent = (
    <>
      <div className={classes.rank}>{getRanking(data).value}</div>
      <ProgressBar value={getRanking(data).percent} color={color} />
    </>
  );
  let compContent;
  if (competitors.length > 0) {
    const compComponents = [
      { component: singleContent, url },
      ...competitors.map(({ criteria }, index) => {
        if (!hasCritData(criteria.alexa)) {
          return null;
        }
        return {
          component: (
            <>
              <div className={classes.rank}>{getRanking(criteria.alexa.data).value}</div>
              <ProgressBar value={getRanking(criteria.alexa.data).percent} color={secondary[index]} />
            </>
          ),
          url: criteria.alexa.url
        };
      })
    ];
    compContent = <CriteriumCompetitors competitors={compComponents} />;
  }

  return (
    <Criterium
      criterium={criterium}
      variables={{ domain: url, url }}
    >
      {compContent || singleContent}
    </Criterium>
  );
};

Alexa.propTypes = {
  competitors: PropTypes.arrayOf(
    PropTypes.shape({
      criteria: PropTypes.shape({
        alexa: PropTypes.shape({
          data: PropTypes.number,
          url: PropTypes.string
        })
      }),
      color: PropTypes.string
    })
  ),
  criterium: PropTypes.shape({
    data: PropTypes.number
  })
};

export default React.memo(Alexa);
